import { create } from 'zustand';
import { api } from '@/lib/api';
import type { PostWithAuthor, CommentWithAuthor } from '@shared/types';

interface FeedState {
  posts: PostWithAuthor[];
  loading: boolean;
  hasMore: boolean;
  offset: number;
  sort: 'recent' | 'top';

  setSort: (sort: 'recent' | 'top') => void;
  fetchFeed: (reset?: boolean) => Promise<void>;
  fetchUserPosts: (username: string, reset?: boolean) => Promise<void>;
  createPost: (content: string, options?: { visibility?: string; wall_owner_id?: string; image_key?: string }) => Promise<void>;
  toggleLike: (postId: string) => Promise<void>;
  addComment: (postId: string, content: string) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
}

export const useFeedStore = create<FeedState>((set, get) => ({
  posts: [],
  loading: false,
  hasMore: true,
  offset: 0,
  sort: 'recent',

  setSort: (sort) => {
    set({ sort, posts: [], offset: 0, hasMore: true });
    get().fetchFeed(true);
  },

  fetchFeed: async (reset = false) => {
    const state = get();
    if (state.loading) return;

    const offset = reset ? 0 : state.offset;
    set({ loading: true });

    try {
      const posts = await api.get<PostWithAuthor[]>(`/posts?limit=20&offset=${offset}&sort=${state.sort}`);
      set({
        posts: reset ? posts : [...state.posts, ...posts],
        offset: offset + posts.length,
        hasMore: posts.length === 20,
        loading: false,
      });
    } catch {
      set({ loading: false });
    }
  },

  fetchUserPosts: async (username, reset = false) => {
    const state = get();
    if (state.loading) return;

    const offset = reset ? 0 : state.offset;
    set({ loading: true });

    try {
      const posts = await api.get<PostWithAuthor[]>(`/users/${username}/posts?limit=20&offset=${offset}`);
      set({
        posts: reset ? posts : [...state.posts, ...posts],
        offset: offset + posts.length,
        hasMore: posts.length === 20,
        loading: false,
      });
    } catch {
      set({ loading: false });
    }
  },

  createPost: async (content, options = {}) => {
    await api.post<any>('/posts', {
      content,
      visibility: options.visibility || 'public',
      wall_owner_id: options.wall_owner_id || null,
      image_key: options.image_key || null,
    });
    // Refetch to get full post with author
    get().fetchFeed(true);
  },

  toggleLike: async (postId) => {
    const { posts } = get();
    // Optimistic update
    set({
      posts: posts.map((p) =>
        p.id === postId
          ? {
              ...p,
              liked_by_me: !p.liked_by_me,
              like_count: p.liked_by_me ? p.like_count - 1 : p.like_count + 1,
            }
          : p
      ),
    });

    try {
      await api.post(`/posts/${postId}/like`);
    } catch {
      // Revert on error
      set({
        posts: posts,
      });
    }
  },

  addComment: async (postId, content) => {
    await api.post(`/posts/${postId}/comments`, { content });
    // Bump comment count optimistically
    const { posts } = get();
    set({
      posts: posts.map((p) =>
        p.id === postId ? { ...p, comment_count: p.comment_count + 1 } : p
      ),
    });
  },

  deletePost: async (postId) => {
    await api.del(`/posts/${postId}`);
    set({ posts: get().posts.filter((p) => p.id !== postId) });
  },
}));
